// propertySlice.js

import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import axios from 'axios';
import { handleApiError } from '../../utils/route-guard/errorHandling';
import { transformDlsData } from '../../utils/transformDlsData';

export const fetchProperty = createAsyncThunk(
  'properties/fetchProperty',
  async ({ url, method = 'POST', payload, token }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Authorization: token,
          'Content-Type': 'application/json'
        }
      };

      const response = method === 'POST' ? await axios.post(url, payload, config) : await axios.get(url, config);

      return response.data.data;
    } catch (error) {
      handleApiError(error);
      const message = error.response ? error.response.data : error.message;
      return rejectWithValue(message);
    }
  }
);

// Fetch raw DLS data and transform client-side
export const fetchPropertyRaw = createAsyncThunk(
  'properties/fetchPropertyRaw',
  async ({ payload, token }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          Authorization: token,
          'Content-Type': 'application/json'
        }
      };

      const response = await axios.post('/api/dls/external_property_raw', payload, config);

      // Transform the raw data client-side
      const transformedData = transformDlsData(response.data.data, payload.block, payload.regist_no);

      return transformedData;
    } catch (error) {
      handleApiError(error);
      const message = error.response?.data?.message || error.message;
      return rejectWithValue({ message });
    }
  }
);

const initialState = {
  property: null,
  status: 'idle',
  error: null
};

export const propertySlice = createSlice({
  name: 'properties',
  initialState,
  reducers: {
    resetProperty: () => initialState,
    setProperty: (state, action) => {
      state.status = 'succeeded';
      state.property = action.payload;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProperty.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchProperty.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.property = action.payload;
      })
      .addCase(fetchProperty.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload.message || action.error.message;
      })
      .addCase(fetchPropertyRaw.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchPropertyRaw.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.property = action.payload;
      })
      .addCase(fetchPropertyRaw.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.message || action.error.message;
      });
  }
});

export default propertySlice.reducer;

const selectPropertyState = (state) => state.properties.property;

export const { resetProperty, setProperty } = propertySlice.actions;

export const selectCoordsGeom = createSelector([selectPropertyState], (property) => {
  return property?.parcel_characteristics?.coords_geom;
});

export const selectPolygonGeom = createSelector([selectPropertyState], (property) => {
  return property?.parcel_characteristics?.polygon_geom;
});
